import { ref, computed } from "vue";
import dayjs from "dayjs";
import { httpRequest } from "@follaw-fr/utils/request.utils";
import { handleNetworkError } from "@follaw-fr/utils/handleNetworkError";
import { PRESET_FEATURES, features } from "@follaw-fr/utils/features";
import { useFeaturesStores } from "@follaw-fr/composables/useFeaturesStores";
import type { FiltersByFeature } from "@follaw-fr/utils/filter.utils";

export function useFeaturesFilters() {
  const { featuresStores } = useFeaturesStores();

  const featuresLoading = ref<string[]>([]);
  const featuresFilters = ref<FiltersByFeature>({});

  const getFeatureFilters = async (key: string) => {
    if (!Object.keys(featuresFilters.value).includes(key)) {
      featuresLoading.value.push(key);
      try {
        let payload: {
          get_filters?: boolean;
          size?: number;
          filters?: boolean;
          start?: any;
          end?: any;
        } = {
          get_filters: true,
          size: 20,
        };
        if (key === PRESET_FEATURES.PETITIONS) {
          delete payload.get_filters;
          payload.filters = true;
        }
        if (key === PRESET_FEATURES.AGENDA) {
          delete payload.size;
          payload.start = dayjs();
          payload.end = dayjs();
        }
        if (features[key]?.additionalPayload) {
          payload = { ...payload, ...features[key].additionalPayload };
        }
        const response = await httpRequest("post", features[key].url, payload);
        featuresFilters.value[key] = (
          response.data as { filters: any }
        ).filters;
      } catch (err) {
        handleNetworkError(err);
      } finally {
        featuresLoading.value.splice(featuresLoading.value.indexOf(key));
      }
    }
  };

  const filtersToExclude = [
    "from",
    "size",
    "get_filters",
    "filters",
    "start",
    "end",
    "sort",
    "sort_field",
    "sort_order",
    "sort_value",
    "highlight",
  ];

  const activeFiltersByFeature = computed(() => {
    const groupedFilters: { [key: string]: any } = {};
    for (const [feature, { active_filters }] of Object.entries(features)) {
      active_filters.forEach((filter) => {
        const featureKey = feature as keyof typeof featuresStores;
        const filterKey =
          filter as keyof (typeof featuresStores)[typeof featureKey];
        if (featuresStores[featureKey][filterKey]) {
          groupedFilters[featureKey] = {
            ...(typeof featuresStores[featureKey][filterKey] === "object"
              ? featuresStores[featureKey][filterKey]
              : {}),
          };
          filtersToExclude.forEach((filterToExclude) => {
            delete groupedFilters[featureKey][filterToExclude];
          });
        }
      });
    }
    return groupedFilters;
  });

  return {
    featuresLoading,
    featuresFilters,
    activeFiltersByFeature,
    getFeatureFilters,
  };
}
